// Copyright (c) Jupyter Development Team.
// Distributed under the terms of the Modified BSD License.
import { showDialog, Dialog } from '@jupyterlab/apputils';
import { IMainMenu } from '@jupyterlab/mainmenu';
import { ITranslator } from '@jupyterlab/translation';
import { retroIcon } from '@retrolab/ui-components';
import * as React from 'react';
/**
 * A list of resources to show in the help menu.
 */
const RESOURCES = [
    {
        text: 'About Jupyter',
        url: 'https://jupyter.org'
    },
    {
        text: 'Markdown Reference',
        url: 'https://commonmark.org/help/'
    }
];
/**
 * The command IDs used by the help plugin.
 */
var CommandIDs;
(function (CommandIDs) {
    CommandIDs.open = 'help:open';
    CommandIDs.shortcuts = 'help:shortcuts';
    CommandIDs.about = 'help:about';
})(CommandIDs || (CommandIDs = {}));
/**
 * The help plugin.
 */
const plugin = {
    id: '@retrolab/help-extension:plugin',
    autoStart: true,
    requires: [ITranslator],
    optional: [IMainMenu],
    activate: (app, translator, menu) => {
        const { commands } = app;
        const trans = translator.load('retrolab');
        commands.addCommand(CommandIDs.open, {
            label: args => args['text'],
            execute: args => {
                const url = args['url'];
                window.open(url);
            }
        });
        commands.addCommand(CommandIDs.shortcuts, {
            label: trans.__('Keyboard Shortcuts'),
            execute: () => {
                const title = (React.createElement("span", { className: "jp-AboutRetro-about-header" },
                    React.createElement("div", { className: "jp-AboutRetro-about-header-info" }, trans.__('Keyboard Shortcuts'))));
                const body = (React.createElement("table", { className: "jp-AboutRetro-shortcuts" },
                    React.createElement("thead", null,
                        React.createElement("tr", null,
                            React.createElement("th", null, trans.__('Name')),
                            React.createElement("th", null, trans.__('Shortcut')))),
                    React.createElement("tbody", null, commands.keyBindings
                        .filter(binding => commands.isEnabled(binding.command))
                        .map((binding, i) => (React.createElement("tr", { key: i },
                        React.createElement("td", null, commands.label(binding.command)),
                        React.createElement("td", null,
                            React.createElement("pre", null, binding.keys.join(', ')))))))));
                return showDialog({
                    title,
                    body,
                    buttons: [
                        Dialog.createButton({
                            label: trans.__('Dismiss'),
                            className: 'jp-AboutRetro-about-button jp-mod-reject jp-mod-styled'
                        })
                    ]
                });
            }
        });
        commands.addCommand(CommandIDs.about, {
            label: trans.__('About %1', app.name),
            execute: () => {
                const title = (React.createElement(React.Fragment, null,
                    React.createElement("span", { className: "jp-AboutRetro-header" },
                        React.createElement(retroIcon.react, { height: "256px", width: "auto" }))));
                const retroNotebookURL = 'https://github.com/jupyterlab/retrolab';
                const linkLabel = trans.__('RETROLAB ON GITHUB');
                const externalLinks = (React.createElement("span", null,
                    React.createElement("a", { href: retroNotebookURL, target: "_blank", rel: "noopener noreferrer", className: "jp-Button-flat jp-AboutRetro-about-externalLinks" }, linkLabel)));
                const version = trans.__('Version: %1', app.version);
                const body = (React.createElement(React.Fragment, null,
                    React.createElement("span", { className: "jp-AboutRetro-body" }, version),
                    React.createElement("div", null, externalLinks)));
                const dialog = new Dialog({
                    title,
                    body,
                    buttons: [
                        Dialog.createButton({
                            label: trans.__('Dismiss'),
                            className: 'jp-AboutRetro-about-button jp-mod-reject jp-mod-styled'
                        })
                    ]
                });
                dialog.addClass('jp-AboutRetro');
                void dialog.launch();
            }
        });
        const resourcesGroup = RESOURCES.map(args => ({
            args,
            command: CommandIDs.open
        }));
        if (menu) {
            menu.helpMenu.addGroup([{ command: CommandIDs.about }]);
            menu.helpMenu.addGroup([{ command: CommandIDs.shortcuts }]);
            menu.helpMenu.addGroup(resourcesGroup);
        }
    }
};
export default plugin;
